import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BoltIcon from "@mui/icons-material/Bolt";
import SafeImage, { pickImage } from "../Global/SafeTmage";
import { fetchUpcomingCached, readCache } from "./toursApi";

const WINDOW_DAYS = 30;   // "last minute" = departing within this many days
const MAX_CARDS = 12;
const TZ = "Asia/Kolkata";

// Calendar-day helpers pinned to IST
const istDay = (d) => new Date(d).toLocaleDateString("en-CA", { timeZone: TZ }); // "2026-10-09"
const dayDiff = (d) =>
  Math.round((Date.parse(istDay(d)) - Date.parse(istDay(new Date()))) / 86400000);

// A departure counts only if it has not started yet (same rule as the tour page)
const isUpcoming = (d) => new Date(d).getTime() > Date.now();

// Dynamic: built from real upcoming group departures, nothing hard-coded.
function LastMinuteEscapes() {
  // Parse a server payload into { rows, mode }; past departures are always dropped
  const parse = (data) => ({
    rows: (data?.data || []).filter(({ tour, dep }) => tour && dep && isUpcoming(dep.departure_date)),
    mode: data?.mode === "soon" ? "soon" : "upcoming",
  });

  // Paint instantly from the last visit, then refresh from the server
  const cached = useMemo(() => {
    const c = readCache("upcoming", 10 * 60 * 1000);
    return c ? parse(c) : null;
  }, []);
  const [items, setItems] = useState(cached ? cached.rows : null);
  const [mode, setMode] = useState(cached ? cached.mode : "soon"); // "soon" = inside window, "upcoming" = fallback
  const scrollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchUpcomingCached({ days: WINDOW_DAYS, limit: MAX_CARDS })
      .then((data) => {
        if (!alive) return;
        const { rows, mode: m } = parse(data);
        setMode(m);
        setItems(rows);
      })
      .catch(() => alive && setItems((prev) => prev || []));
    return () => { alive = false; };
  }, []);

  // nothing yet / nothing upcoming → render nothing (no flash or layout jump)
  if (!items || items.length === 0) return null;

  const scrollBy = (dx) => scrollRef.current?.scrollBy({ left: dx, behavior: "smooth" });

  return (
    <section className="bg-gradient-to-br from-[#0B2A5B] via-[#12397A] to-[#1D4ED8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-sky-300 mb-1">
              <BoltIcon sx={{ fontSize: 15 }} />
              {mode === "soon" ? `Departing in the next ${WINDOW_DAYS} days` : "Next group departures"}
            </p>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
              {mode === "soon" ? "Last-Minute Escapes" : "Upcoming Escapes"}
            </h3>
          </div>
          <div className="hidden md:flex gap-2">
            <button type="button" aria-label="Scroll left" onClick={() => scrollBy(-320)} className="w-9 h-9 rounded-full bg-white/10 border border-white/30 text-white hover:bg-white hover:text-blue-700 hover:border-white flex items-center justify-center transition"><ChevronLeftIcon fontSize="small" /></button>
            <button type="button" aria-label="Scroll right" onClick={() => scrollBy(320)} className="w-9 h-9 rounded-full bg-white/10 border border-white/30 text-white hover:bg-white hover:text-blue-700 hover:border-white flex items-center justify-center transition"><ChevronRightIcon fontSize="small" /></button>
          </div>
        </div>

        <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-2 overscroll-x-contain" style={{ scrollbarWidth: "none", touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch" }}>
          {items.map(({ tour, dep }, idx) => {
            const n = dayDiff(dep.departure_date);
            const seats =
              dep.show_slots_on_user_side !== false
                ? dep.available_seats ?? (dep.total_seats - dep.booked_seats)
                : null;
            const orig = Number(tour.original_price || 0);
            const price = Number(tour.display_price || tour.price || 0);
            const off = orig > price ? Math.round(((orig - price) / orig) * 100) : 0;
            return (
              <Link
                key={`${tour._id || tour.id}-${dep._id}`}
                to={`/tours/${tour._id || tour.id}`}
                className="group shrink-0 min-w-[220px] max-w-[220px] bg-white rounded-2xl p-3 border border-white/60 shadow-[0_8px_20px_-6px_rgba(2,12,40,0.45)] hover:border-sky-300 transition-colors"
              >
                <div className="relative h-28 rounded-xl overflow-hidden bg-slate-100">
                  <div className="absolute inset-0 [&>*]:w-full [&>*]:h-full [&_img]:w-full [&_img]:h-full [&_img]:object-cover">
                    <SafeImage src={pickImage(tour.image_url)} width={400} loading={idx < 4 ? "eager" : "lazy"} decoding="async" type="tour" alt={tour.title} compact />
                  </div>

                  <span className="absolute top-2 left-2 z-10 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                    {n <= 0 ? "DEPARTS TODAY" : n === 1 ? "TOMORROW" : `IN ${n} DAYS`}
                  </span>
                  {off > 0 && (
                    <span className="absolute top-2 right-2 z-10 bg-white text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded">
                      {off}% OFF
                    </span>
                  )}
                </div>
                <div className="pt-3 px-1">
                  <p className="text-[11px] text-slate-500 font-medium truncate">{tour.location} · {tour.duration}</p>
                  <h4 className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 mt-0.5 min-h-[2.5rem] group-hover:text-blue-700 transition-colors">{tour.title}</h4>
                  <div className="mt-3 flex items-end justify-between">
                    <div>
                      <p className="text-[11px] text-slate-500">
                        {new Date(dep.departure_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: TZ })}
                        {seats != null && seats <= 10 ? ` · ${seats} seats left` : ""}
                      </p>
                      <p className="text-base font-extrabold text-blue-900">
                        ₹{price.toLocaleString("en-IN")}
                        <span className="text-[10px] font-medium text-slate-500"> /adult</span>
                      </p>
                    </div>
                    <span className="text-xs font-bold text-blue-600 group-hover:underline">Book →</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default React.memo(LastMinuteEscapes);